import { useEffect, useMemo, useState } from "react";
import { Box, Button, Checkbox, Chip, CircularProgress, Dialog, IconButton, InputAdornment, Tab, Tabs, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useTenantVegetationTypes } from "../../../api/tenantVegetationType";
import { TenantVegetationType } from "../../../types/tenantVegetationType";
import { UnitSystem } from "../../../utils/units";
import ModelClassSelect, { McFilterSelect } from "./ModelClassSelect";
import { Sparkline } from "./FuelCurve";
import {
  McGroup, defaultMc, loadAt, loadToInput, loadUnit, matchesMcFilter, mcShort, mcValue, nameKey, num, plural, r1, validateRow, yearsTo,
} from "./vtModel";
import {
  BG, FAINT, LINE, LINE_STRONG, MUTED, PANEL, RED_TEXT, TEAL, TEXT,
  btnGhost, btnPlain, btnPrimary, btnSmall, checkboxSx, chipSx, fieldSx, iconBtnSx, labelSx, linkBtnSx,
} from "./vtTheme";

const PAGE = 60;

export interface BlankType {
  name: string;
  mc: string;
  max: string;
  /** The exact t/ha behind ``max`` (see VtRow.maxT). */
  maxT?: number;
  rate: string;
}

interface AddVegetationTypesDialogProps {
  open: boolean;
  onClose: () => void;
  classes: string[];
  groups: McGroup[];
  unit: UnitSystem;
  /** ``nameKey`` of every type in the project (draft included). */
  projectNames: Set<string>;
  onAddLibrary: (items: TenantVegetationType[]) => void;
  onAddBlank: (row: BlankType) => void;
}

const tabsSx = {
  minHeight: 44,
  "& .MuiTab-root": { minHeight: 44, px: 0.25, mr: 3, minWidth: 0, textTransform: "none", fontWeight: 500, fontSize: 14, color: MUTED, "&.Mui-selected": { color: TEXT } },
  "& .MuiTabs-indicator": { bgcolor: TEAL },
};

/** Add types from the library (several at once) or start a blank one. */
export default function AddVegetationTypesDialog({ open, onClose, classes, groups, unit, projectNames, onAddLibrary, onAddBlank }: AddVegetationTypesDialogProps) {
  const { data: libraryResp, isLoading } = useTenantVegetationTypes(open);
  const library = useMemo(() => libraryResp?.data ?? [], [libraryResp]);
  const libraryByName = useMemo(() => new Map(library.map((e) => [nameKey(e.veg_class), e])), [library]);

  const [tab, setTab] = useState<"library" | "blank">("library");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("*");
  const [pages, setPages] = useState(1);
  const [picked, setPicked] = useState<Record<number, boolean>>({});
  // A new type starts with a fire model; "Doesn't burn" has to be picked.
  const firstMc = defaultMc(groups);
  const [blank, setBlank] = useState<BlankType>({ name: "", mc: firstMc, max: loadToInput(10, unit), maxT: 10, rate: "0.1" });

  useEffect(() => {
    if (!open) return;
    setTab("library"); setQuery(""); setFilter("*"); setPages(1); setPicked({});
    setBlank({ name: "", mc: firstMc, max: loadToInput(10, unit), maxT: 10, rate: "0.1" });
  }, [open, unit, firstMc]);

  const unitLabel = loadUnit(unit);
  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    const m = library.filter((e) => {
      const mc = mcValue(e.model_class_name, e.model_subclass);
      if (!matchesMcFilter(mc, filter)) return false;
      return !q || e.veg_class.toLowerCase().includes(q) || mcShort(mc).toLowerCase().includes(q);
    });
    // Names that start with the search come first.
    return q ? [...m.filter((e) => e.veg_class.toLowerCase().startsWith(q)), ...m.filter((e) => !e.veg_class.toLowerCase().startsWith(q))] : m;
  }, [library, q, filter]);
  const shown = matches.slice(0, PAGE * pages);
  const yMax = Math.max(10, ...shown.map((e) => Number(e.max_fuel_load) || 0));
  const pickedItems = library.filter((e) => picked[e.id]);
  const toggle = (id: number) => setPicked((p) => { const n = { ...p }; if (n[id]) delete n[id]; else n[id] = true; return n; });

  const countText = (q || filter !== "*" ? plural(matches.length, "match", "matches") : `${library.length.toLocaleString()} types in your library`)
    + (matches.length > shown.length ? ` · showing ${shown.length}` : "");

  const blankErrs = validateRow(blank, unit);
  const blankKey = nameKey(blank.name);
  const blankDup = !!blankKey && projectNames.has(blankKey);
  const blankLib = blankKey && !blankDup ? libraryByName.get(blankKey) : undefined;
  const bL = num(blank.max);
  const bK = num(blank.rate);
  const blankOk = !blankErrs.max && !blankErrs.rate;
  const blankInvalid = Object.keys(blankErrs).length > 0 || blankDup;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="add-vt-title"
      PaperProps={{ sx: { bgcolor: BG, color: TEXT, border: `1px solid ${LINE_STRONG}`, borderRadius: 3.5, height: "min(760px, calc(var(--vh100) - 32px))", display: "flex", flexDirection: "column", overflow: "hidden" } }}
    >
      <Box sx={{ p: "20px 22px 0", display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id="add-vt-title" sx={{ fontSize: 17, fontWeight: 600 }}>Add vegetation types</Typography>
          <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.5 }}>Pick one or more from your library, or start from a blank type. Added types stay as drafts until you save.</Typography>
        </Box>
        <IconButton sx={iconBtnSx} aria-label="Close" onClick={onClose}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>
      <Box sx={{ px: "22px", pt: 1, borderBottom: `1px solid ${LINE}` }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={tabsSx}>
          <Tab value="library" label={<Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>From library <Box sx={chipSx("plain")}>{library.length.toLocaleString()}</Box></Box>} />
          <Tab value="blank" label="Blank type" />
        </Tabs>
      </Box>

      {tab === "library" ? (
        <>
          <Box sx={{ p: "14px 22px 10px", display: "flex", flexWrap: "wrap", gap: 1.25, alignItems: "center" }}>
            <TextField
              autoFocus
              size="small"
              type="search"
              placeholder="Search by name or model class, e.g. sclerophyll"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPages(1); }}
              sx={{ flex: "1 1 280px" }}
              slotProps={{
                input: { sx: fieldSx(), startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: FAINT }} /></InputAdornment> },
                htmlInput: { "aria-label": "Search vegetation library" },
              }}
            />
            <McFilterSelect value={filter} classes={classes} onChange={(v) => { setFilter(v); setPages(1); }} ariaLabel="Filter library by model class" />
          </Box>
          <Typography sx={{ px: "22px", pb: 1, fontSize: 12, color: MUTED }} aria-live="polite">{isLoading ? "Loading your library…" : countText}</Typography>
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", mx: "22px", border: `1px solid ${LINE}`, borderRadius: 2.5, bgcolor: PANEL }}>
            {isLoading && <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={24} sx={{ color: TEAL }} /></Box>}
            {shown.map((e) => {
              const mc = mcValue(e.model_class_name, e.model_subclass);
              const inProject = projectNames.has(nameKey(e.veg_class));
              const on = !!picked[e.id];
              const L = Number(e.max_fuel_load);
              const k = Number(e.accumulation_rate);
              return (
                <Box
                  component="label"
                  key={e.id}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.75, p: "10px 16px", minHeight: 58, borderBottom: `1px solid ${LINE}`,
                    cursor: inProject ? "default" : "pointer", bgcolor: on ? "rgba(0,212,173,0.08)" : "transparent",
                    "&:hover": { bgcolor: inProject ? undefined : on ? "rgba(0,212,173,0.12)" : "rgba(255,255,255,0.04)" },
                  }}
                >
                  <Checkbox size="small" sx={checkboxSx} checked={on || inProject} disabled={inProject} onChange={() => toggle(e.id)} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 500, lineHeight: 1.35, color: inProject ? MUTED : TEXT }}>{e.veg_class}</Typography>
                    <Box sx={{ display: "flex", gap: 1.25, alignItems: "center", mt: 0.5, fontSize: 12, color: MUTED, flexWrap: "wrap", fontVariantNumeric: "tabular-nums" }}>
                      <Box sx={{ ...chipSx("plain"), height: 20 }}>{mcShort(mc)}</Box>
                      <span>Max {loadToInput(L, unit)} {unitLabel}</span>
                      <span>k {k}</span>
                      <span>90% at {yearsTo(k, 10)}</span>
                    </Box>
                  </Box>
                  {inProject && <Box sx={chipSx("plain")}>In project</Box>}
                  <Sparkline max={L} rate={k} yMax={yMax} stroke={inProject ? "rgba(255,255,255,0.3)" : TEAL} fill="rgba(0,212,173,0.1)" width={84} height={26} />
                </Box>
              );
            })}
            {matches.length > shown.length && (
              <Box sx={{ p: 1.5, textAlign: "center" }}>
                <Button sx={{ ...btnPlain, ...btnSmall }} onClick={() => setPages((p) => p + 1)}>Show {Math.min(PAGE, matches.length - shown.length)} more</Button>
              </Box>
            )}
            {!isLoading && matches.length === 0 && (
              <Box sx={{ p: "48px 24px", textAlign: "center" }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{library.length ? "Nothing in your library matches" : "Your library is empty"}</Typography>
                <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.75 }}>{library.length ? "Try another search, or create a blank type." : "Create a blank type instead."}</Typography>
                <Button
                  sx={{ ...btnPlain, ...btnSmall, mt: 1.75 }}
                  onClick={() => { setTab("blank"); setBlank((b) => ({ ...b, name: query.trim() })); }}
                >
                  {query.trim() ? `Create “${query.trim()}” as a blank type` : "Create a blank type"}
                </Button>
              </Box>
            )}
          </Box>
          <Box sx={{ p: "14px 22px", mt: 1.75, borderTop: `1px solid ${LINE}`, display: "flex", flexWrap: "wrap", gap: 1.25, alignItems: "center" }}>
            {pickedItems.length > 0 ? (
              <>
                <Typography sx={{ fontSize: 13, fontWeight: 600, mr: 0.25 }}>{pickedItems.length} selected</Typography>
                {pickedItems.slice(0, 3).map((e) => (
                  <Chip
                    key={e.id}
                    size="small"
                    label={e.veg_class}
                    title={e.veg_class}
                    onDelete={() => toggle(e.id)}
                    sx={{ maxWidth: 220, bgcolor: "rgba(255,255,255,0.08)", color: TEXT, border: `1px solid ${LINE_STRONG}`, "& .MuiChip-deleteIcon": { color: MUTED, "&:hover": { color: TEXT } } }}
                  />
                ))}
                {pickedItems.length > 3 && <Typography sx={{ fontSize: 12, color: MUTED }}>+{pickedItems.length - 3} more</Typography>}
                <Button sx={linkBtnSx} onClick={() => setPicked({})}>Clear</Button>
              </>
            ) : (
              <Typography sx={{ fontSize: 13, color: MUTED }}>Library parameters are copied in and can be edited before you save.</Typography>
            )}
            <Box sx={{ flex: 1 }} />
            <Button sx={btnGhost} onClick={onClose}>Cancel</Button>
            <Button sx={btnPrimary} disabled={!pickedItems.length} onClick={() => onAddLibrary(pickedItems)}>
              {pickedItems.length ? `Add ${plural(pickedItems.length, "type")}` : "Add types"}
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: "20px 22px", display: "flex", flexDirection: "column", gap: 2.25, maxWidth: 560 }}>
            <Box>
              <Box component="label" htmlFor="blank-name" sx={labelSx}>Name</Box>
              <TextField
                id="blank-name"
                autoFocus
                fullWidth
                size="small"
                placeholder="e.g. Coastal Banksia Scrub"
                value={blank.name}
                onChange={(e) => setBlank({ ...blank, name: e.target.value })}
                sx={{ mt: 0.75 }}
                slotProps={{ input: { sx: fieldSx({ err: blankDup }) } }}
              />
              {blankDup && <Typography sx={{ fontSize: 12, color: RED_TEXT, mt: 0.75 }}>This project already has a type with this name.</Typography>}
              {blankLib && (
                <Box sx={{ fontSize: 12, color: MUTED, mt: 1 }}>
                  “{blankLib.veg_class}” is in your library ·{" "}
                  <Button
                    sx={linkBtnSx}
                    onClick={() => setBlank({
                      name: blankLib.veg_class, mc: mcValue(blankLib.model_class_name, blankLib.model_subclass),
                      max: loadToInput(Number(blankLib.max_fuel_load), unit), maxT: Number(blankLib.max_fuel_load), rate: String(blankLib.accumulation_rate),
                    })}
                  >
                    Use library parameters
                  </Button>
                </Box>
              )}
            </Box>
            <Box>
              <Box component="label" htmlFor="blank-mc" sx={labelSx}>Model class</Box>
              <Box sx={{ mt: 0.75 }}><ModelClassSelect id="blank-mc" variant="field" value={blank.mc} groups={groups} onChange={(mc) => setBlank({ ...blank, mc })} /></Box>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.75 }}>
              {([["blank-max", `Max fuel load (${unitLabel})`, "max", blankErrs.max], ["blank-rate", "Accumulation rate (k)", "rate", blankErrs.rate]] as const).map(([id, label, field, err]) => (
                <Box key={id}>
                  <Box component="label" htmlFor={id} sx={labelSx}>{label}</Box>
                  <TextField
                    id={id}
                    fullWidth
                    size="small"
                    value={blank[field]}
                    onChange={(e) => setBlank({ ...blank, [field]: e.target.value })}
                    sx={{ mt: 0.75 }}
                    slotProps={{ input: { sx: { ...fieldSx({ err: !!err }), "& input": { textAlign: "right" } } }, htmlInput: { inputMode: "decimal" } }}
                  />
                  {err && <Typography sx={{ fontSize: 12, color: RED_TEXT, mt: 0.75 }}>{err}</Typography>}
                </Box>
              ))}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, bgcolor: "rgba(0,0,0,0.2)", border: `1px solid ${LINE}`, borderRadius: 2, p: "10px 12px" }}>
              {blankOk
                ? <Sparkline max={bL} rate={bK} yMax={Math.max(10, bL)} stroke={TEAL} fill="rgba(0,212,173,0.1)" width={160} height={48} />
                : <Box sx={{ width: 160, height: 48 }} />}
              <Box sx={{ fontSize: 13, lineHeight: 1.6 }}>
                <div><Box component="span" sx={{ color: MUTED }}>Reaches 90% of max</Box> <strong>{blankOk ? yearsTo(bK, 10) : "—"}</strong></div>
                <div><Box component="span" sx={{ color: MUTED }}>Load at 10 yrs</Box> <strong>{blankOk ? `${r1(loadAt(bL, bK, 10))} ${unitLabel}` : "—"}</strong></div>
              </Box>
            </Box>
          </Box>
          <Box sx={{ p: "14px 22px", borderTop: `1px solid ${LINE}`, display: "flex", flexWrap: "wrap", gap: 1.25, alignItems: "center" }}>
            <Typography sx={{ fontSize: 13, color: MUTED }}>You can change the map colour in the side panel after adding.</Typography>
            <Box sx={{ flex: 1 }} />
            <Button sx={btnGhost} onClick={onClose}>Cancel</Button>
            <Button sx={btnPrimary} disabled={blankInvalid} onClick={() => onAddBlank(blank)}>Add type</Button>
          </Box>
        </>
      )}
    </Dialog>
  );
}
